import { formatParisDateTime, UNIFIED_ORDER_STATUS, type UnifiedOrderListItem } from '@flux/shared';
import { AlertTriangle } from 'lucide-react';
import type { JSX } from 'react';
import { createDataTableColumnHelper } from '@/components/data-table/data-table.js';
import { OriginBadge } from '@/components/filters/origin-badge.js';
import { Badge, StatusBadge } from '@/components/ui/badge.js';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip.js';
import { InvoiceRowActions } from '@/features/invoices/invoice-row-actions.js';
import { OrderRowActions } from './order-row-actions.js';
import { V1Badge } from './v1-badge.js';

const helper = createDataTableColumnHelper<UnifiedOrderListItem>();

function StatusCell({ item }: { item: UnifiedOrderListItem }): JSX.Element {
  return (
    <div className="flex flex-col items-start gap-0.5">
      <span className="flex items-center gap-1.5">
        <StatusBadge registry={UNIFIED_ORDER_STATUS} code={item.status} />
        {item.simulated ? <Badge tone="orange">Simulé</Badge> : null}
        <V1Badge handledByV1={item.handledByV1} />
        {item.warnings.length > 0 ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className="inline-flex size-5 cursor-default items-center justify-center text-status-orange-fg"
                aria-label={`${String(item.warnings.length)} avertissement(s)`}
              >
                <AlertTriangle className="size-4" aria-hidden="true" />
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <ul className="flex flex-col gap-1">
                {item.warnings.map((warning) => (
                  <li key={`${warning.code}-${warning.message}`}>{warning.message}</li>
                ))}
              </ul>
            </TooltipContent>
          </Tooltip>
        ) : null}
      </span>
      {item.statusHint ? (
        <span className="text-xs tabular-nums text-muted-foreground">
          {item.statusHint}
        </span>
      ) : null}
    </div>
  );
}

/**
 * Colonnes de l'écran Commandes (`02` §3, refonte du 10/09/2026) : Origine ·
 * Numéro · Client · Date · Statut · Actions, pour les commandes Shopify comme
 * pour les factures Pennylane. `sortableColumnIds` doit reprendre les `id`
 * ci-dessous qui correspondent aux tris de `GET /api/orders/unified`
 * (`http/api/orders/unified-list.ts::SORTS`).
 */
export const orderColumns = helper.columns([
  helper.display({
    id: 'origin',
    header: 'Origine',
    cell: ({ row }) => (
      <OriginBadge code={row.original.origin.code} label={row.original.origin.label} />
    ),
  }),
  helper.accessor('number', {
    id: 'number',
    header: 'Numéro',
    cell: (info) => (
      <span className="whitespace-nowrap font-medium tabular-nums">{info.getValue()}</span>
    ),
  }),
  helper.display({
    id: 'customer',
    header: 'Client',
    cell: ({ row }) => {
      const item = row.original;
      return <span className="break-words">{item.customerName ?? item.email ?? '—'}</span>;
    },
  }),
  helper.accessor('date', {
    id: 'date',
    header: 'Date',
    cell: (info) => (
      <span className="whitespace-nowrap tabular-nums">
        {formatParisDateTime(new Date(info.getValue()))}
      </span>
    ),
  }),
  helper.display({
    id: 'status',
    header: 'Statut',
    cell: ({ row }) => <StatusCell item={row.original} />,
  }),
  helper.display({
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => {
      const item = row.original;
      return item.origin.kind === 'pennylane' ? (
        <InvoiceRowActions item={item} />
      ) : (
        <OrderRowActions item={item} />
      );
    },
  }),
]);

/** Colonnes triables — doivent correspondre exactement aux `sortBy` de `GET /api/orders/unified`. */
export const ORDER_SORTABLE_COLUMN_IDS = ['origin', 'number', 'date'] as const;
